import {HttpStatusCode} from "@angular/common/http";
import {displayBoolAsYesNo} from "@ui-kit/utils/static/string-format-util";
import {
  DisplayFunction,
  DisplayOrder,
  String,
  TableDisplayedDefault,
  TableIdentifier
} from "@ui-webkit/components/crud-item/crud-item-decorator";
import {
  alphanumericPattern,
  emailPattern,
  numberPattern,
  UploadDataModel,
  UploadResponse
} from "@ui-webkit/components/csv-import/csv-import.model";

export interface BillingResponse {
  billingDetails: BatchRetailerResponse[];
  report: UploadResponse
}

export interface BatchRetailerResponse {
  retailerCompanyName: string;
  retailerId: number;
  status: HttpStatusCode;
  errorMessages: string[];
}

export interface BillingUpload {
  retailerId: number;
  retailerCompanyName: string;

  billingEmail: string;
  billingName: string;
  billingPhoneNum: string;
  billingFormattedAddress: string;
  billingVatReg: string;
  billingCompReg: string;
  address: any;
  addressPlace: any;

  bankingAccountName: string;
  bankingAccountType: string; // CHEQUE / SAVING
  bankingBranchCode: string; // BANK NAME
  bankingAccountNumber: string;
  bankingMethodType: string; // PAYBACK_TRANSACT

  errorMessages: string[];
}


export interface BankOption {
  bankName: string;
  centralBranchCode: string;
}

export const bankListWithDefaultBranchCode: BankOption[] = [
  {
    bankName: 'Absa Bank',
    centralBranchCode: '632005'
  },
  {
    bankName: 'Capitec Bank',
    centralBranchCode: '470010'
  },
  {
    bankName: 'First National Bank (South Africa)',
    centralBranchCode: '250655'
  },
  {
    bankName: 'Investec Bank',
    centralBranchCode: '580105'
  },
  {
    bankName: 'Nedbank (South Africa)',
    centralBranchCode: '198765'
  },
  {
    bankName: 'Nedbank Corporate Saver Account',
    centralBranchCode: '720026'
  },
  {
    bankName: 'Postbank',
    centralBranchCode: '460005'
  },
  {
    bankName: 'Standard Bank (South Africa)',
    centralBranchCode: '51001'
  },
  {
    bankName: 'African Bank',
    centralBranchCode: '430000'
  },
  {
    bankName: 'Albaraka Bank',
    centralBranchCode: '800000'
  },
  {
    bankName: 'Bank Of Namibia',
    centralBranchCode: '980172'
  },
  {
    bankName: 'Bidvest Bank',
    centralBranchCode: '462005'
  },
  {
    bankName: 'Central Bank Of Lesotho',
    centralBranchCode: '586611'
  },
  {
    bankName: 'Citi Bank',
    centralBranchCode: '350005'
  },
  {
    bankName: 'Discovery Bank',
    centralBranchCode: '679000'
  },
  {
    bankName: 'Finbond Mutual Bank',
    centralBranchCode: '589000'
  },
  {
    bankName: 'First National Bank Lesotho',
    centralBranchCode: '280061'
  },
  {
    bankName: 'First National Bank Namibia',
    centralBranchCode: '282672'
  },
  {
    bankName: 'First National Bank Swaziland',
    centralBranchCode: '287364'
  },
  {
    bankName: 'Grinrod Bank',
    centralBranchCode: '584000'
  },
  {
    bankName: 'Hsbc Bank',
    centralBranchCode: '587000'
  },
  {
    bankName: 'Jp Morgan Chase Bank',
    centralBranchCode: '432000'
  },
  {
    bankName: 'Meeg Bank',
    centralBranchCode: '471001'
  },
  {
    bankName: 'Capitec Business Banking/Mercantile Bank',
    centralBranchCode: '450105'
  },
  {
    bankName: 'Mtn Banking',
    centralBranchCode: '490991'
  },
  {
    bankName: 'Standard Bank Namibia',
    centralBranchCode: '87373'
  },
  {
    bankName: 'State Bank Of India',
    centralBranchCode: '801000'
  },
  {
    bankName: 'Ubank',
    centralBranchCode: '431010'
  },
  {
    bankName: 'Unibank',
    centralBranchCode: '790005'
  },
  {
    bankName: 'Vbs Mutual Bank',
    centralBranchCode: '588000'
  },
  {
    bankName: 'Olympus Mobile Bank',
    centralBranchCode: '585001'
  },
  {
    bankName: 'Tyme Bank',
    centralBranchCode: '678910'
  }
];

export const bankAccountTypes: AccountType[] = [
  {code: '1', type: 'Cheque'},
  {code: '2', type: 'Saving'}
];

export interface AccountType {
  type: string;
  code: string;
}

export const billingDetailsUploadModel: UploadDataModel[] = [
  {
    property: 'retailerId',
    label: 'Retailer ID',
    required: false,
    validators: [numberPattern],
    validationMessage: '',
    description: '',
    selectColumn: false,
    showInTable: false,
    computeValue: false
  },
  {
    property: 'retailerCompanyName',
    label: 'Company Name',
    required: true,
    validators: [],
    validationMessage: 'Requires a valid company name.',
    description: '',
    selectColumn: true,
    showInTable: true,
    computeValue: false
  },
  {
    property: 'billingEmail',
    label: 'Billing Email',
    required: true,
    validators: [emailPattern],
    validationMessage: 'Requires a valid email address',
    description: '',
    selectColumn: true,
    showInTable: true,
    computeValue: false
  },
  {
    property: 'billingName',
    label: 'Billing Name',
    required: true,
    validators: [],
    validationMessage: 'Requires a valid name',
    description: '',
    selectColumn: true,
    showInTable: true,
    computeValue: false
  },
  {
    property: 'billingPhoneNum',
    label: 'Phone Number',
    required: true,
    validators: [],
    validationMessage: 'Requires a valid phone number',
    description: '',
    selectColumn: true,
    showInTable: true,
    computeValue: false
  },
  {
    property: 'billingFormattedAddress',
    label: 'Billing Address',
    required: true,
    validators: [],
    validationMessage: 'Requires a google maps address.',
    description: 'A google maps address. e.g. 15A Cradock Ave, Rosebank, Johannesburg, 2196',
    selectColumn: true,
    showInTable: true,
    computeValue: false
  },
  {
    property: 'address',
    label: 'Billing Address',
    required: false,
    validators: [],
    validationMessage: '',
    description: '',
    selectColumn: false,
    showInTable: false,
    computeValue: true
  },
  {
    property: 'addressPlace',
    label: 'Billing Address Place',
    required: false,
    validators: [],
    validationMessage: '',
    description: '',
    selectColumn: false,
    showInTable: false,
    computeValue: true
  },
  {
    property: 'billingVatReg',
    label: 'VAT Reg. Number',
    required: true,
    validators: [],
    validationMessage: 'Requires a valid VAT Registration Number',
    description: 'Use N/A if not applicable',
    selectColumn: true,
    showInTable: true,
    computeValue: false
  },
  {
    property: 'billingCompReg',
    label: 'Company Reg. Number',
    required: true,
    validators: [],
    validationMessage: 'Requires a valid company Registration Number',
    description: 'Use N/A if not applicable',
    selectColumn: true,
    showInTable: true,
    computeValue: false
  },
  {
    property: 'bankingAccountName',
    label: 'Account Name',
    required: true,
    validators: [],
    validationMessage: 'Requires a valid account name.',
    description: '',
    selectColumn: true,
    showInTable: true,
    computeValue: false
  },
  {
    property: 'bankingAccountTypeCode',
    label: 'Account Type',
    required: true,
    validators: [alphanumericPattern],
    validationMessage: 'Requires a valid account type.',
    description: 'Use "Cheque" or "Saving"',
    selectColumn: true,
    showInTable: true,
    computeValue: false
  },
  {
    property: 'bankingAccountType',
    label: 'Account Type',
    required: false,
    validators: [numberPattern],
    validationMessage: '',
    description: '',
    selectColumn: false,
    showInTable: false,
    computeValue: true
  },
  {
    property: 'bankingBankName',
    label: 'Bank Name',
    required: true,
    validators: [],
    validationMessage: 'Requires a valid bank name',
    description: 'Enter the bank name from the provided list',
    selectColumn: true,
    showInTable: true,
    computeValue: false
  },
  {
    property: 'bankingBranchCode',
    label: 'Branch Code',
    required: false,
    validators: [alphanumericPattern],
    validationMessage: '',
    description: '',
    selectColumn: false,
    showInTable: false,
    computeValue: true
  },
  {
    property: 'bankingAccountNumber',
    label: 'Account Number',
    required: true,
    validators: [alphanumericPattern],
    validationMessage: 'Requires a valid account number',
    description: '',
    selectColumn: true,
    showInTable: true,
    computeValue: false
  },
  {
    property: 'bankingMethodType',
    label: 'Payment Method',
    required: false,
    validators: [alphanumericPattern],
    validationMessage: 'Requires a valid payment method',
    description: 'Use TRANSACT_PAYBACK for tenants',
    selectColumn: false,
    showInTable: false,
    computeValue: true
  },
  {
    property: 'errorMessages',
    label: 'Error',
    required: false,
    validators: [],
    validationMessage: '',
    description: '',
    selectColumn: false,
    showInTable: true,
    computeValue: false
  },
]

export class RetailerBillingSummary {
  @TableIdentifier
  @String('Retailer ID')
  @DisplayOrder(1)
  @TableDisplayedDefault
  retailerId?: number;

  @String('Company Name')
  @DisplayOrder(2)
  @TableDisplayedDefault
  retailerCompanyName?: number;

  @String("Billing Found")
  @DisplayFunction(displayBoolAsYesNo)
  @DisplayOrder(3)
  @TableDisplayedDefault
  billingFound?: boolean;

  @String("Banking Found")
  @DisplayFunction(displayBoolAsYesNo)
  @DisplayOrder(4)
  @TableDisplayedDefault
  bankingFound?: boolean;

  constructor(obj: any = {}) {
    obj = obj || {};
    this.retailerId = obj.retailerId;
    this.retailerCompanyName = obj.retailerCompanyName;
    this.billingFound = obj.billingFound;
    this.bankingFound = obj.bankingFound;
  }
}
